import { Reactive } from 'vue';
import CryptoJS from 'crypto-js';
import { noLogin } from '@config/routes';
import systemInfo from '@/stores/systemInfo';
import projectData from '@/stores/projectData';
import { navigateTo, getRoute, urlReg, parseUrl } from '@utils/router';

/** AES加密解密 */
export const AES = {
	getSecretKey() {
		let key = '6sa175asc@qq.com';
		let iv = '6549846516589321';

		if (['ios', 'android'].includes(systemInfo().plat)) {
			key = '9ji1y5amc@qq.com';
			iv = '9240740519579012';
		}

		return {
			key: CryptoJS.enc.Utf8.parse(key),
			iv: CryptoJS.enc.Utf8.parse(iv),
		};
	},
	encrypt(data: string): string {
		data = String(data);
		const { key, iv } = this.getSecretKey();

		return CryptoJS.AES.encrypt(data, key, {
			iv,
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7,
		}).toString();
	},
	decrypt(data: string): string {
		const { key, iv } = this.getSecretKey();
		return CryptoJS.AES.decrypt(data, key, {
			iv,
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7,
		}).toString(CryptoJS.enc.Utf8);
	},
};

/** 获取当前打包平台 */
export const getPlatform = (): 'h5' | 'mp' | 'ios' | 'android' => {
	let plat;
	// #ifdef H5
	plat = 'h5';
	// #endif
	// #ifdef MP
	plat = 'mp';
	// #endif
	// #ifdef APP-PLUS
	plat = uni.getSystemInfoSync().platform;
	// #endif
	return plat;
};

/** 合并对象 */
export const assignObj = (obj1: any, obj2: any) => {
	for (const key in obj2)
		key in obj1 && ![undefined, null, ''].includes(obj2[key]) && (obj1[key] = obj2[key]);
	return obj1;
};

/** 检查更新 */
export const checkUpdated = async () => {
	// #ifdef MP
	const updateManager = uni.getUpdateManager();
	updateManager.onCheckForUpdate(function (res) {
		// 请求完新版本信息的回调
		console.log(res.hasUpdate);
	});
	updateManager.onUpdateReady(() => {
		uni.showModal({
			title: '更新提示',
			content: '新版本已经准备好，请重启应用',
			showCancel: false,
			success() {
				updateManager.applyUpdate();
			},
		});
	});
	// #endif
	// #ifndef MP
	await projectData().fetchVersionData();
	// #endif
	// #ifdef APP-PLUS
	projectData()
		.fetchVersionData()
		.then((info) => {
			const { desc, isForce, isNew, updateUrl, content } = info;
			if (isNew != 1 && updateUrl) {
				uni.showModal({
					content,
					title: desc,
					showCancel: isForce != 1,
					cancelText: '稍后进行',
					confirmText: '立即更新',
					success: ({ confirm }) => {
						if (confirm) {
							if (systemInfo().plat == 'android') {
								uni.showLoading({
									mask: true,
									title: '正在下载更新...',
								});
								toUpdate(updateUrl);
							} else {
								navigateTo(updateUrl);
							}
						}
					},
				});
			}
		});
	// #endif
};

/** 下载更新 */
const toUpdate = (updateUrl: string) => {
	uni.downloadFile({
		url: updateUrl,
		success(res) {
			if (res.statusCode === 200) {
				uni.hideLoading();
				uni.showToast({
					title: '下载完成',
					icon: 'success',
				});
				plus.runtime.install(res.tempFilePath);
			} else {
				uni.hideLoading();
				uni.showToast({
					icon: 'error',
					title: '下载失败',
				});
			}
		},
		fail(res) {
			uni.hideLoading();
			uni.showToast({
				icon: 'error',
				title: '下载失败',
			});
		},
	});
};

/** 登录 */
export const toLogin = (type = 'push') => {
	return new Promise((resolve, reject) => {
		const { path } = getRoute();

		if (path.includes('login')) return;

		uni.showModal({
			title: '提示',
			content: '请先登录',
			showCancel: true,
			success: ({ confirm, cancel }) => {
				if (confirm) {
					navigateTo('/pages/mycenter/login/index', type);
					resolve(null);
				}
				if (cancel) {
					reject();
				}
			},
		});
	});
};

/** 商户登录 */
export const toMerchantLogin = debounce((url?: string) => {
	uni.showModal({
		title: '提示',
		content: '请先登录商户端',
		showCancel: true,
		success: ({ confirm, cancel }) => {
			if (confirm) {
				let path = '/pages/shop/login';
				url && (path += `?url=${url}`);
				navigateTo(path);
			}
		},
	});
}, 300);

/** 解析链接码 */
export const parseCode = (code: string) => {
	if (urlReg.test(code)) {
		const { query } = parseUrl(code);
		return query?.code;
	}
	return code;
};

export const getDeviceId = () => {
	const { deviceId } = uni.getSystemInfoSync();
	return AES.encrypt(deviceId);
};

export const isWxBrowser = () => {
	// #ifdef H5
	const ua = navigator.userAgent.toLowerCase();
	return ua.includes('micromessenger');
	// #endif
	// #ifndef H5
	return false;
	// #endif
};

export const chooseLocation = async (params: Reactive<any>) => {
	try {
		const { latitude, longitude, address } = await uni.chooseLocation();
		const { area, city, detail, province } = await request.post(port.REGION_DISTINGUISHADDRESS, {
			text: address,
		});
		const data = {
			lat: latitude,
			lng: longitude,
			area,
			city,
			detail,
			province,
		};
		assignObj(params, data);
	} catch {
		uni.showToast({
			title: '获取地址失败',
			icon: 'none',
		});
	}
};

export const confirmModal = (desc: string, confirm, cancel?) => {
	uni.showModal({
		title: '提示',
		content: desc,
		showCancel: true,
		success: ({ confirm: _confirm, cancel: _cancel }) => {
			_confirm && confirm?.();
			_cancel && cancel?.();
		},
	});
};

export const getOpenId = async (msg = '支付失败'): Promise<string> => {
	try {
		let openid = uni.getStorageSync(WECHAT_OPENID);
		if (openid) return openid;

		const { code } = await uni.login();
		const res = await request.post(port.WECHAT_CODETOPENID, { code });
		openid = res.openid;
		uni.setStorageSync(WECHAT_OPENID, openid);

		return openid;
	} catch {
		uni.showToast({
			title: msg,
			icon: 'none',
		});
		return Promise.reject();
	}
};

const amapKey = '34a8dcae3fe8ccedc299b4229b8d02f1';

export const getRegeocode = (lat, lng): Promise<any> => {
	return new Promise((resolve) => {
		uni.request({
			url: 'https://restapi.amap.com/v3/geocode/regeo?parameters',
			data: {
				key: amapKey,
				extensions: 'all',
				location: [lng, lat].join(),
			},
			success: ({ data: { regeocode } }: any) => {
				const { addressComponent, formatted_address, aois } = regeocode;
				const { province, district, township, streetNumber, citycode, adcode, country } =
					addressComponent;
				let { city } = addressComponent;
				const { street, number } = streetNumber;
				const address = township + street + number;
				city = city.length > 0 ? city : province;
				resolve({
					lat,
					lng,
					city,
					adcode,
					address,
					citycode,
					country,
					district,
					province,
					desc: formatted_address,
					name: aois[0]?.name || address,
				});
			},
		});
	});
};

export const getPriceDesc = (price, orderType?) => {
	if (typeof price == 'object') {
		orderType = price.orderType;
		price = price.marketPrice;
	}
	// 竞价、礼包、兑换
	// if ([1, 30, 42].includes(Number(orderType))) {
	// 	return `${price}积分`;
	// }
	return `￥${price}`;
};

/**
 * 将传入的尺寸转换为相应的单位尺寸
 * @param num 要转换的尺寸
 * @param usePx 是否使用px
 * @returns 转换后的尺寸
 */
export const addUnit = (num: string | number, usePx = false) => {
	if (typeof num == 'number' || !isNaN(Number(num))) {
		return num + (usePx ? 'px' : 'rpx');
	}

	if (/rpx$/i.test(num)) {
		return usePx ? `${uni.upx2px(parseFloat(num))}px` : num;
	}

	if (/px$/i.test(num)) {
		return usePx ? num : `${parseFloat(num) / (uni.upx2px(100) / 100)}rpx`;
	}

	return num;
};

export const toService = async (url?) => {
	await projectData().fetchConfig();
	const { wechatUrl } = projectData().config;

	// #ifdef MP
	wx.openCustomerServiceChat({
		extInfo: {
			url: url || wechatUrl || 'https://work.weixin.qq.com/kfid/kfcfe1bd7bbfc3375bf',
		},
		corpId: 'wwb1972c4801eb50b8',
		success(res) {
			console.log(`%c [ res ]:`, `color: #fff; background: blue; font-size: 13px;`, res);
		},
		fail(err) {
			console.log(`%c [ err ]:`, `color: #fff; background: blue; font-size: 13px;`, err);
		},
	});
	// #endif
	// #ifndef MP
	navigateTo(url || '/pages/services/help/list');
	// #endif
};

export const requestSubscribeMessage = async (tmplId, desc) => {
	// #ifdef MP
	const prjData = projectData();
	await getOpenId();
	await prjData.fetchUserInfo('init');

	const { cancel } = await uni.showModal({
		title: '提示',
		content: `${desc}，将会为您发送订阅消息`,
		showCancel: false,
	});

	if (cancel) {
		return;
	}

	uni.requestSubscribeMessage({
		tmplIds: [tmplId],
	});
	// #endif
};
